/**
 * tabs — the client-record tab bodies (PR-C1; record-structure rebuild
 * 2026-07-06). All render the ONE aggregate payload except the Notes tab
 * (which mounts the LIVE note-pipeline components against notesApi).
 *
 *   OverviewTab      — THE DOOR (design review U3, adopted): read-only.
 *                      Latest activity as prose sentences (left, prominent
 *                      — the founder's ask), a one-line meds summary, the
 *                      next appointment, risk if any. NO Edit/Archive/Add
 *                      anywhere; every item LINKS into its owning tab
 *                      (edits are one click away — this supersedes the
 *                      earlier "editable Overview" ask; flagged to the
 *                      founder).
 *   CareRecordTab    — owns the CLINICAL picture (full edit anatomy):
 *                      allergies pin, appointments band, meds + functional
 *                      status (left), risk + conditions + hospital (right).
 *                      Contacts + providers moved OUT (People owns them),
 *                      and the care-record restructure moved the four
 *                      non-medical registers OUT to Life & logistics —
 *                      quiet cross-links remain. Every card lives on the
 *                      record, an empty one reading as no entries (LP-29).
 *   LifeLogisticsTab — the client's practical world: Legal & wishes +
 *                      Housing (left), Insurance + Addresses (right) —
 *                      the SAME four existing cards off the same aggregate
 *                      snapshot (no new fetch), same gate as Care.
 *   NotesActivityTab — LIVE: NoteComposer + ConfirmQueue + NotesFeed.
 *   PeopleTab        — owns contacts + care providers + the referral line.
 *   DocumentsTab     — LIVE (D8 v1, 0131): upload / list / download /
 *                      version / remove against documentsApi (own file;
 *                      re-exported below).
 *   PreBillingTab    — LIVE (native billing v0): month billing review +
 *                      the biller's CSV (PreBilling.tsx).
 *
 * Tokens only. No fetch in Overview/Care/People (the PR-C2 contract).
 */

import { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useHashScroll } from '@/lib/useHashScroll';
import { clientCallName } from '@/lib/clientCallName';
import { type NoteWire } from '@/lib/notes';
import type { NoteDraftWire } from '@/lib/noteDrafts';
import { NoteComposer } from '@/components/notes/NoteComposer';
import { EMPTY_SPICE_DECLINE_FACTS, SpiceDeclineForm } from '@/components/aiTools/SpiceDeclineForm';
import { NoteDraftsList } from '@/components/notes/NoteDraftsList';
import { ConfirmQueue } from '@/components/notes/ConfirmQueue';
import { NotesFeed } from '@/components/notes/NotesFeed';
import { Button } from '@/components/primitives/Button';
import { CheckboxRow } from '@/components/primitives/CheckboxRow';
import { EmailModeToggle } from '@/components/aiTools/EmailModeToggle';
import type { SpiceDeclineFacts } from '@/lib/spiceVisitNote';
import {
  canSwitchRecordServiceSide,
  defaultRecordServiceSide,
  type RecordServiceSide,
} from '@/lib/recordServiceSide';
import type { ClientEngagement } from '@/lib/clientRecord';
import {
  AllergiesCard,
  MedicationsCard,
  ConditionsCard,
  HospitalEventsCard,
  RiskFlagsCard,
  LegalInstrumentsCard,
  CoverageCard,
  AppointmentsCard,
  ContactsCard,
  ProvidersCard,
  FunctionalStatusCard,
} from '@/components/clientRecord/cardVariants';
import { AddressesCard } from '@/components/clientRecord/AddressesCard';
import { ArchivedItemsPage } from '@/components/clientRecord/cardSpine';
import { InHomeSupportCard } from '@/components/clientRecord/InHomeSupportCard';
import { PreBilling } from '@/components/clientRecord/PreBilling';
import { RatesCard } from '@/components/clientRecord/RatesCard';
import { BillingDetailsCard } from '@/components/clientRecord/BillingDetailsCard';
import { ReferredByDialog } from '@/components/clientRecord/ReferredByDialog';
import { NotRecorded } from '@/components/clientRecord/VitalsBand';
import { useTenant } from '@/lib/auth';
import {
  canEditClientDetails,
  canViewInvoice,
  canWriteClinicalNotes,
  canWriteDemoClinical,
} from '@/lib/capabilities';
import type { ClientRecordSnapshot } from '@/lib/clientRecord';

/** Persistent light underline — links navigate (R1). */
const LINK_CLS =
  'text-sage-700 underline decoration-sage-700/30 underline-offset-2 hover:decoration-sage-700 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight';

// ---------------------------------------------------------------------
// Care record — owns the clinical projections (U3: one home per object;
// contacts live in People, cross-linked below; the care team gets a
// PRESENCE card here — A8). LP-29 (owner ruling 2026-10-24): every card
// lives on the record, empty or not. An empty card keeps its heading,
// reads as no entries in one quiet line, and carries its own Add; the
// "Not yet recorded" cluster that once gathered a tab's empty sections
// is gone. One rule for every card on every tab.
// ---------------------------------------------------------------------

/** A column entry: the card node under its stable key. */
type ColumnEntry = { key: string; node: ReactNode };

/** A column's cards, in document order. */
function column(entries: ColumnEntry[]): ReactNode {
  return entries.map((e) => <Fragment key={e.key}>{e.node}</Fragment>);
}

/** The grid every tab's cards sit in: one column on a phone, in document
 *  order, two from lg up. Every card is always present (LP-29), so
 *  neither column ever empties and no half-width blank sits beside a
 *  survivor. */
const TWO_COL_GRID_CLS = 'grid grid-cols-1 gap-4 items-start lg:grid-cols-2';


export function CareRecordTab({ record }: { record: ClientRecordSnapshot }): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  // G4 (Soak #2): consume #risk-flags (the top-bar risk line + the
  // AttentionBand) + the jump-strip section hashes — scroll the target
  // card into view on this tab. block:'start' so a card TALLER than the
  // viewport lands header-first (the card sections carry scroll-margin
  // clearing the sticky header + strip); the #note-<id> notes-feed
  // consumer keeps the hook's default 'center'.
  useHashScroll(true, [], 'start');

  // Care-record restructure (founder-approved): the Care record keeps
  // ONLY the clinical picture. Left column = medications + functional
  // status; right column = risk flags, conditions, hospital & rehab.
  // The four non-medical registers (Legal & wishes, Insurance, Housing,
  // Addresses) moved to the Life & logistics tab below.
  const left: ColumnEntry[] = [
    {
      key: 'medications',
      node: <MedicationsCard rows={record.medications} providers={record.providers} record={record} />,
    },
    // The tenth card (0122) sits at the BOTTOM of its column (founder
    // soak ruling 2026-07-06).
    { key: 'functional-status', node: <FunctionalStatusCard rows={record.functionalStatus} /> },
  ];
  const right: ColumnEntry[] = [
    { key: 'risk-flags', node: <RiskFlagsCard rows={record.riskFlags} /> },
    { key: 'conditions', node: <ConditionsCard rows={record.conditions} /> },
    // D7 (Soak #2 #10): the CareTeamCard is REMOVED from the Care record
    // (its 2026-07-10 addition is reversed) — the care team's one home is
    // the People tab now; the People cross-link below is the door.
    // Appointments lead the record from the top (Soak #2 #11), not here.
    { key: 'hospital-events', node: <HospitalEventsCard rows={record.hospitalEvents} /> },
  ];
  return (
    <div data-testid="tab-care" className="flex flex-col gap-4">
      <ArchivedItemsPage>
        {/* Allergies pin to the TOP of the record, full width, ALWAYS
            visible — even empty (WS-3: an empty list is itself safety
            information). 0137 §5 adds the third state: confirmed
            no-known-allergies. */}
        <AllergiesCard rows={record.allergies} noneKnownOn={record.allergiesNoneKnownOn} />
        {/* Appointments lead (Soak #2 #11): a full-width band above the two
            columns, so the next visit is the first thing read on the record.
            Empty, it stays (LP-29), its own doors opening the
            visit-or-appointment dialog. */}
        <AppointmentsCard rows={record.appointments} providers={record.providers} clientName={clientCallName(record.vitals)} />
        <div data-testid="care-columns" className={TWO_COL_GRID_CLS}>
          <div className="flex flex-col gap-4 min-w-0">{column(left)}</div>
          <div className="flex flex-col gap-4 min-w-0">{column(right)}</div>
        </div>
        {/* Cross-links — no duplicate management surfaces. U3: People owns
            contacts AND the care team (its only home now, D7). Care-record
            restructure: the practical registers (legal, insurance, addresses
            with their type of place) live one tab over in Life & logistics. */}
        <div className="flex flex-col gap-1 px-1 text-sm text-ink-soft">
          <p data-testid="care-people-crosslink">
            Contacts and care-team management live in{' '}
            <Link to={`/t/${tenantSlug}/clients/${clientId}?tab=people`} className={LINK_CLS}>
              People <span aria-hidden="true">↗</span>
            </Link>
          </p>
          <p data-testid="care-life-logistics-crosslink">
            Legal &amp; wishes, insurance and addresses live in{' '}
            <Link
              to={`/t/${tenantSlug}/clients/${clientId}?tab=life-logistics`}
              className={LINK_CLS}
            >
              Life &amp; logistics <span aria-hidden="true">↗</span>
            </Link>
          </p>
        </div>
        {/* The camera door onto the record rides the Medications and Care
            providers cards themselves (one photo feeds both), empty or
            filled, so no stray prompt line sits below the columns (LP-29). */}
      </ArchivedItemsPage>
    </div>
  );
}

// ---------------------------------------------------------------------
// Life & logistics — the client's practical world (care-record
// restructure): Legal & wishes + Housing (left), Insurance + Addresses
// (right). Composes the SAME four existing cards off the same aggregate
// snapshot the Care record reads — no new fetch, same clinical gate.
// Every card lives on the record, an empty one reading as no entries
// (LP-29); a quiet cross-link leads back to the clinical picture.
// ---------------------------------------------------------------------

export function LifeLogisticsTab({ record }: { record: ClientRecordSnapshot }): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const left: ColumnEntry[] = [
    { key: 'legal-instruments', node: <LegalInstrumentsCard rows={record.legalInstruments} /> },
  ];
  const right: ColumnEntry[] = [
    { key: 'coverage', node: <CoverageCard rows={record.coverage} /> },
    // Addresses (0137, WS-4 D2): home + "currently at" beside coverage.
    // Empty, the card's own "+ Add address" is the door (its dialog
    // starts with Primary checked when the client has none).
    {
      key: 'addresses',
      node: <AddressesCard addresses={record.addresses} pronouns={record.vitals.pronouns} />,
    },
    // In-home support (0235): the client's kinds, after Addresses where its
    // line was. Empty (nothing ticked) it reads "Not recorded" with its
    // Edit; the card and the edit are edit_client_details.
    { key: 'in-home-support', node: <InHomeSupportCard inHomeSupport={record.inHomeSupport} /> },
  ];
  return (
    <div data-testid="tab-life-logistics" className="flex flex-col gap-4">
      <ArchivedItemsPage>
        <div data-testid="life-logistics-columns" className={TWO_COL_GRID_CLS}>
          <div className="flex flex-col gap-4 min-w-0">{column(left)}</div>
          <div className="flex flex-col gap-4 min-w-0">{column(right)}</div>
        </div>
        {/* The gentle door back to the clinical picture (the restructure's
            Care ↔ Life & logistics pairing). */}
        <p data-testid="life-logistics-care-crosslink" className="px-1 text-sm text-ink-soft">
          The clinical picture (medications, conditions, allergies, and more) lives in{' '}
          <Link to={`/t/${tenantSlug}/clients/${clientId}?tab=care`} className={LINK_CLS}>
            Care record <span aria-hidden="true">↗</span>
          </Link>
        </p>
      </ArchivedItemsPage>
    </div>
  );
}

// S4 (launch-UX, founder-ruled 2026-08-23): OverviewTab, LatestActivityCard,
// and OverviewRail are RETIRED — the single-author record has no reader to
// brief (the activity log read the author her own words back) and the rail
// duplicated the hero + the Care record. The record lands on Notes &
// activity; provenance chips on record cards carry the note-to-fact
// linkage; next appointment lives in the band.

// Mirror of the reduced-motion check in src/components/hub/HubQuote.tsx.
// Defined locally (tiny, and the call sites evolve independently).
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// ---------------------------------------------------------------------
// Notes & activity — the ONLY live surface. Mounts the existing note-
// pipeline components against notesApi (unchanged; already audited).
// ---------------------------------------------------------------------
export function NotesActivityTab({
  clientId,
  clientIsDemo = false,
  engagement,
  serviceSide,
  onServiceSideChange,
  viewerIsCareManager = false,
  clientName = null,
  composerFocusRequest = 0,
  onComposerFocused,
}: {
  clientId: string;
  /** LP-42: above 0, the header's "+ Case Note" (or "+ Session Note") was
   *  clicked. The note box scrolls into view, its text box takes focus,
   *  and onComposerFocused hands the request back. */
  composerFocusRequest?: number;
  onComposerFocused?: () => void;
  /** The name the client goes by (clientCallName), threaded to NotesFeed. */
  clientName?: string | null;
  /** clients.is_demo (0156) — when true, a demo-only author (board_observer)
   *  gets the note-authoring surfaces on THIS client. Defaults false. */
  clientIsDemo?: boolean;
  /** clients.engagement. A 'both' client shows a Care management/SPICE
   *  toggle at the top of this tab (recordServiceSide.ts remembers the
   *  pick per client) and reads `serviceSide` to decide which face is
   *  current; a SPICE-only viewer gets no toggle and holds the SPICE
   *  face. 'spice' and 'care_management' show their one face, no
   *  toggle, `serviceSide` unused. */
  engagement?: ClientEngagement;
  /** The 'both' client's current face — owned by the page (ClientRecord)
   *  so the header's "+ Case Note" / "+ Session Note" button stays in
   *  sync with the toggle here. Required whenever engagement is 'both'. */
  serviceSide?: RecordServiceSide;
  /** Fires when the viewer flips the toggle. */
  onServiceSideChange?: (side: RecordServiceSide) => void;
  /** record.vitals.viewerIsCareManager — passed to the confirm queue,
   *  which carries it to the care-notice label. Defaults false. */
  viewerIsCareManager?: boolean;
}): ReactNode {
  // CM-home deep link (?tab=notes&kind=assessment): the new-client
  // "Add it" nudge opens the composer on its assessment door. Mount-time
  // read; the composer owns the toggle from there.
  const [searchParams] = useSearchParams();
  const initialKind = searchParams.get('kind') === 'assessment' ? 'assessment' : 'note';
  // 0125 edit-as-new-version: the feed's Edit hands the note here; the
  // composer remounts (key) in supersede mode prefilled from it.
  const [editingNote, setEditingNote] = useState<NoteWire | null>(null);
  // 0130 post-a-draft: "Your drafts" Open hands the draft here; the
  // composer remounts (key) prefilled and posting consumes it
  // server-side. Mutually exclusive with the supersede mode.
  const [postingDraft, setPostingDraft] = useState<NoteDraftWire | null>(null);
  // "The client declined today" (SPICE face, LP-28): a checkbox line
  // above the composer. Ticked, the decline form shows under it and hands
  // the decline to the composer through the note prefill; the composer
  // remounts (a new key) to pick it up. The three facts live here, not in
  // the form, so unticking hides the form and ticking again shows the
  // same words back; Add to the note moves them into the note and clears
  // them here.
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineFacts, setDeclineFacts] = useState<SpiceDeclineFacts>(EMPTY_SPICE_DECLINE_FACTS);
  const [composerSeed, setComposerSeed] = useState(0);
  const composerHostRef = useRef<HTMLDivElement | null>(null);
  function scrollToComposer(): void {
    composerHostRef.current?.scrollIntoView?.({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  }
  // Board-observer split (0152): a read-only viewer (read_clinical_record,
  // so the Notes tab OPENS, but NOT write_clinical_notes) sees only the
  // read-only NotesFeed — the composer, drafts, confirm queue, and the
  // per-note Edit door all author into the clinical record, so they hide.
  //
  // Board demo sandbox (0156): a demo-only author (board_observer holds
  // write_demo_clinical, NOT write_clinical_notes) gets those same authoring
  // surfaces ON A DEMO CLIENT (clientIsDemo) — the founder wants the board to
  // post a note here and watch the pipeline populate the record. The server
  // fences every one of these writes to is_demo clients regardless (the
  // _clinicalWriteGuard repo fence), so this is the affordance mirror.
  const { whoami, tenantSlug } = useTenant();
  const canWrite = canWriteClinicalNotes(whoami) || (canWriteDemoClinical(whoami) && clientIsDemo);
  // LP-42: the header's note button lands on the note box, whether the tab
  // was already Notes or has just mounted, and wherever the page was
  // scrolled. Focus waits for no scroll (preventScroll) so a smooth scroll
  // is not cut short by the browser's own jump to the focused box.
  useEffect(() => {
    if (composerFocusRequest <= 0) return;
    if (canWrite) {
      scrollToComposer();
      composerHostRef.current
        ?.querySelector<HTMLTextAreaElement>('#note-composer-text')
        ?.focus({ preventScroll: true });
    }
    onComposerFocused?.();
  }, [composerFocusRequest, canWrite, onComposerFocused]);
  // A client on BOTH services shows a Care management/SPICE toggle above
  // the composer to a viewer who may switch faces; a SPICE-only viewer
  // never gets the case-note door, so she sees no toggle and the SPICE
  // face stands. A pure spice/care_management client has only the one
  // face, so `serviceSide` never applies. Uncontrolled fallback (a viewer
  // default) keeps this component self-sufficient when a host doesn't
  // wire the page-level state up (ClientRecord.tsx does, so the header's
  // note button stays in sync with the pick made here).
  const [internalSide, setInternalSide] = useState<RecordServiceSide>(() =>
    defaultRecordServiceSide(whoami),
  );
  const resolvedSide = serviceSide ?? internalSide;
  const showServiceSideToggle = engagement === 'both' && canSwitchRecordServiceSide(whoami);
  function handleSideChange(next: RecordServiceSide): void {
    setInternalSide(next);
    onServiceSideChange?.(next);
  }
  // The face this tab actually shows: a both-engagement client resolves
  // through the toggle above; spice/care_management show their one face
  // unconditionally (today's behavior, no toggle to resolve).
  const effectiveEngagement: ClientEngagement | undefined =
    engagement === 'both' ? resolvedSide : engagement;
  // The SPICE face carries the decline line (owner ruling 2026-10-24,
  // LP-28): the SPICE email summary is written from the Writing tools, as
  // every other tool is, and the record holds no door into it.
  const isSpiceFace = effectiveEngagement === 'spice';
  // S3.2 (launch-UX, founder-ruled): below lg the DOM order IS the page
  // order — composer, drafts, queue, feed — so the post-then-confirm loop
  // survives on a phone (the queue used to land beneath the entire feed).
  // At lg the explicit grid coords rebuild the exact old layout: the
  // composer+drafts block and the feed stack in column 1, the queue keeps
  // its sticky rail in column 2, bounded to the window and scrolling on
  // its own (see the rail below). No overlays, no bottom
  // sheets (ADR-0017 F1 stays foreclosed).
  return (
    <div
      data-testid="tab-notes"
      // Row 1 is the composer's own height and row 2 takes the rest: the
      // confirm queue spans both rows, and a queue taller than the composer
      // and the notes together must lengthen the notes row, never open a gap
      // between the composer and Notes.
      className="grid gap-6 items-start lg:grid-cols-[minmax(0,1fr)_360px] lg:grid-rows-[auto_1fr]"
    >
      {canWrite && (
        <div
          // scroll-mt clears the sticky header (60/68px) when the note
          // box is scrolled to (block: 'start').
          className="space-y-6 min-w-0 scroll-mt-[76px] md:scroll-mt-[84px] lg:col-start-1 lg:row-start-1"
          ref={composerHostRef}
        >
          {showServiceSideToggle && (
            <EmailModeToggle
              mode={resolvedSide}
              onChange={handleSideChange}
              contextNoun="Notes"
              testId="record-service-toggle"
            />
          )}
          {/* "The client declined today" is one quiet line over the note
              (LP-28): a house checkbox, no card and no pill. Ticked, the
              decline form shows inline beneath it; unticked, the form hides
              and keeps what was typed. */}
          {isSpiceFace && (
            <div className="space-y-3">
              <CheckboxRow
                id="notes-spice-decline"
                testId="notes-spice-decline"
                label="The client declined today"
                checked={declineOpen}
                onChange={setDeclineOpen}
              />
              {declineOpen && (
                <SpiceDeclineForm
                  tenantSlug={tenantSlug}
                  clientId={clientId}
                  facts={declineFacts}
                  onFactsChange={setDeclineFacts}
                  onHandedToComposer={() => {
                    setDeclineOpen(false);
                    setDeclineFacts(EMPTY_SPICE_DECLINE_FACTS);
                    setEditingNote(null);
                    setPostingDraft(null);
                    setComposerSeed((n) => n + 1);
                  }}
                  onCancel={() => setDeclineOpen(false)}
                />
              )}
            </div>
          )}
          <NoteComposer
            key={editingNote?.id ?? postingDraft?.id ?? `new-${composerSeed}`}
            clientId={clientId}
            engagement={effectiveEngagement}
            initialKind={initialKind}
            supersede={editingNote !== null ? { note: editingNote } : null}
            onSupersedeDone={() => setEditingNote(null)}
            onSupersedeCancel={() => setEditingNote(null)}
            serverDraft={editingNote === null ? postingDraft : null}
            onServerDraftDone={() => setPostingDraft(null)}
          />
          <NoteDraftsList
            clientId={clientId}
            onOpenDraft={(draft) => {
              setEditingNote(null);
              setPostingDraft(draft);
              scrollToComposer();
            }}
          />
        </div>
      )}
      {canWrite && (
        <aside
          data-testid="confirm-queue-rail"
          // The rail is a scroll region bounded to the window at lg: it
          // sits under the 68px header and scrolls on its own, so a tall
          // card (a medication with its When boxes; an appointment with
          // its place open) keeps every field and both buttons reachable.
          // No card sets a height or hides overflow. Below lg the queue
          // sits in the page's flow and the page scrolls.
          className="min-w-0 lg:sticky lg:top-[84px] lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto lg:col-start-2 lg:row-start-1 lg:row-span-2"
        >
          <ConfirmQueue clientId={clientId} viewerIsCareManager={viewerIsCareManager} />
        </aside>
      )}
      <div className={canWrite ? 'min-w-0 lg:col-start-1 lg:row-start-2' : 'min-w-0'}>
        <NotesFeed
          clientId={clientId}
          clientName={clientName}
          // The per-note Edit path is a clinical write (supersede) — omit
          // the handler for a read-only viewer so no Edit button renders.
          onEditNote={
            canWrite
              ? (note) => {
                  setPostingDraft(null);
                  setEditingNote(note);
                  scrollToComposer();
                }
              : undefined
          }
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Remaining tab bodies. The TS-6b frame-only placeholders are gone:
// Documents (D8 v1, 0131) and Pre-billing (native billing v0) are both
// LIVE now, and the ComingSoonChip helper retired with them.
// ---------------------------------------------------------------------

/**
 * People — the client's relationships, fixture-backed (B2). Composes the
 * SAME variants the Care record uses (contact + provider — B picks a variant,
 * never invents a card): the contacts register (POA/HCP/emergency/lives-with
 * toggles) + the care providers (3-arm Directory link), plus the referral
 * source line from vitals (shown only when present, mirroring VitalsBand).
 * Edit/archive inherit the spine's stubbed seam; no fetch (fixture-only).
 */
export function PeopleTab({ record }: { record: ClientRecordSnapshot }): ReactNode {
  const { referredBy } = record.vitals;
  const { whoami } = useTenant();
  // The client's own Referred by (0221) is a client-identity write; a
  // holder sees the line even when empty, so an imported client has a door.
  const canEditReferral = canEditClientDetails(whoami);
  const [referralOpen, setReferralOpen] = useState(false);
  // Both registers render, empty or not (LP-29): an empty one reads as
  // no entries with its own Add on the card.
  const entries: ColumnEntry[] = [
    { key: 'contacts', node: <ContactsCard rows={record.contacts} pronouns={record.vitals.pronouns} /> },
    { key: 'providers', node: <ProvidersCard rows={record.providers} /> },
  ];
  return (
    <div data-testid="tab-people" className="flex flex-col gap-4">
      <ArchivedItemsPage>
        {(referredBy !== null || canEditReferral) && (
          <div
            data-testid="people-referred-by"
            className="flex items-baseline gap-2 rounded-card bg-paper shadow-sh1 border border-sage-800/[8%] px-5 py-3 text-sm"
          >
            <span>
              <span className="text-ink-soft">Referred by </span>
              {referredBy !== null ? (
                <>
                  <span className="font-medium text-sage-800">{referredBy.label}</span>
                  {referredBy.source === 'intake' && <span className="text-ink-soft"> (from the intake)</span>}
                </>
              ) : (
                <NotRecorded />
              )}
            </span>
            {canEditReferral && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                data-testid="people-referred-by-edit"
                onClick={() => setReferralOpen(true)}
              >
                Edit
              </Button>
            )}
          </div>
        )}
        {referralOpen && <ReferredByDialog current={referredBy} onClose={() => setReferralOpen(false)} />}
        <div data-testid="people-columns" className={TWO_COL_GRID_CLS}>
          {column(entries)}
        </div>
      </ArchivedItemsPage>
    </div>
  );
}

// DocumentsTab is LIVE (D8 v1, migration 0131) — it owns its fetch +
// mutations, so it lives in its own file (the NotesFeed posture).
// Re-exported here so the tab-body import surface stays one module.
export { DocumentsTab } from '@/components/clientRecord/DocumentsTab';

/**
 * Pre-billing — LIVE (native billing v0): the month's billing review for
 * this client + the biller's CSV. The one tab with its own fetch besides
 * Notes (a different capability population, view_invoice or
 * view_prebilling, reads it, and its month scope is not the aggregate
 * snapshot's shape). The body lives in PreBilling.tsx; `today` is
 * page-injected (no Date.now() here). Billing settings (rates and the
 * bill-to) mount only for a view_invoice holder: a seat with
 * view_prebilling alone never asks for them.
 */
export function PreBillingTab({
  clientId,
  clientName,
  today,
  initialMonth,
  engagement,
}: {
  clientId: string;
  clientName: string;
  today: string;
  /** clients.engagement: which per-visit services the Rates card shows. */
  engagement?: ClientEngagement;
  /** Optional 'YYYY-MM' deep-link (e.g. an invoice draft's edit-at-source
   *  link); PreBilling clamps it to the current month. */
  initialMonth?: string;
}): ReactNode {
  const { whoami } = useTenant();
  const officeSeat = canViewInvoice(whoami);
  return (
    <div data-testid="tab-pre-billing" className="flex flex-col gap-4">
      <ArchivedItemsPage>
        <PreBilling
          clientId={clientId}
          clientName={clientName}
          today={today}
          initialMonth={initialMonth}
        />
        {/* Billing settings (founder feedback 2026-07-10, day-1 polish):
            Rates (0132, B1-D3) + Billing details (the bill-to) side by side
            inside ONE visually distinct block — these are the client's
            standing billing setup, not the month's work above. Tinted
            sage-50 container (AiLimitsTab precedent); the two cards keep
            their own fetches + capability splits (rates write on
            manage_client_rates, Billing details on set_bill_to; view_invoice
            reads). */}
        {officeSeat && (
          <section
            data-testid="billing-settings"
            className="rounded-card bg-sage-50 border border-sage-200 p-4 sm:p-5"
          >
            <h3 className="font-display text-xl text-sage-800 mb-3">Billing settings</h3>
            <div className="grid gap-4 items-start lg:grid-cols-2">
              <RatesCard clientId={clientId} today={today} engagement={engagement} />
              <BillingDetailsCard clientId={clientId} clientName={clientName} />
            </div>
          </section>
        )}
      </ArchivedItemsPage>
    </div>
  );
}
